import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import PublicationsConferences from "../components/sections/PublicationsConferences";
import ContactFooter from "../components/sections/ContactFooter";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  personalInfo,
  expertise,
  technologies,
  stats,
  services,
} from "../data/mock";
import {
  Layers,
  Mountain,
  BrainCircuit,
  Database,
  MapPin,
  BarChart3,
  LineChart,
  TrendingUp,
  FileText,
  Users,
  ArrowDown,
  ExternalLink,
  ChevronRight,
} from "lucide-react";

const iconMap = {
  Layers,
  Mountain,
  BrainCircuit,
  Database,
  MapPin,
  BarChart3,
  LineChart,
  TrendingUp,
  FileText,
  Users,
};

const FadeInSection = ({ children, className = "", delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      } ${className}`}
    >
      {children}
    </div>
  );
};

/* ─── Hero Section ─── */
const HeroSection = () => (
  <section
    id="hero"
    className="relative min-h-screen flex items-center justify-center overflow-hidden"
    style={{ background: "linear-gradient(135deg, #0F1419 0%, #1A2332 50%, #0F1419 100%)" }}
  >
    {/* Subtle grid pattern */}
    <div
      className="absolute inset-0 opacity-[0.04]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
        backgroundSize: "60px 60px",
      }}
    />

    {/* Accent orb */}
    <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-teal-600/10 blur-3xl" />
    <div className="absolute bottom-1/3 left-1/6 w-72 h-72 rounded-full bg-slate-400/5 blur-3xl" />

    <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 text-center">
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-8">
        <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
        <span className="text-white/70 text-sm font-medium">Available for projects</span>
      </div>

      <h1
        className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight"
        style={{ fontFamily: "'Source Serif 4', serif" }}
      >
        {personalInfo.name}
      </h1>

      <p className="text-xl sm:text-2xl text-teal-300/90 font-medium mb-4">
        {personalInfo.title}
      </p>

      <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
        {personalInfo.subtitle}
      </p>

      {/* Stats row */}
      <div className="flex flex-wrap justify-center gap-8 lg:gap-12 mb-12">
        {stats.map((stat, i) => (
          <div key={i} className="text-center">
            <p className="text-2xl lg:text-3xl font-bold text-white">{stat.value}</p>
            <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <a
          href="#publications"
          onClick={(e) => {
            e.preventDefault();
            document.querySelector("#publications")?.scrollIntoView({ behavior: "smooth" });
          }}
          className="flex items-center gap-2 px-8 py-3.5 bg-teal-700 hover:bg-teal-600 text-white font-medium rounded-lg transition-colors duration-200"
        >
          <FileText className="w-4 h-4" />
          Voir les Publications
        </a>
        <a
          href="#contact"
          onClick={(e) => {
            e.preventDefault();
            document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
          }}
          className="flex items-center gap-2 px-8 py-3.5 border border-white/20 text-white hover:bg-white/5 font-medium rounded-lg transition-colors duration-200"
        >
          Contactez-moi
          <ChevronRight className="w-4 h-4" />
        </a>
      </div>

      <div className="mt-16 animate-bounce">
        <ArrowDown className="w-5 h-5 text-slate-500 mx-auto" />
      </div>
    </div>
  </section>
);

/* ─── About Section ─── */
const AboutSection = () => (
  <section id="about" className="py-24 lg:py-32 bg-white">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <FadeInSection>
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">
          {/* Left: Label */}
          <div className="lg:col-span-2">
            <span className="text-sm font-semibold text-teal-700 uppercase tracking-widest">
              À Propos
            </span>
            <h2
              className="text-3xl lg:text-4xl font-bold text-slate-900 mt-3 leading-tight"
              style={{ fontFamily: "'Source Serif 4', serif" }}
            >
              La data science au service des géosciences
            </h2>
            <Separator className="mt-6 bg-teal-200 w-16 h-0.5" />
          </div>

          {/* Right: Content */}
          <div className="lg:col-span-3 space-y-6">
            <p className="text-lg text-slate-600 leading-relaxed">
              {personalInfo.bio}
            </p>
            <p className="text-base text-slate-500 leading-relaxed">
              {personalInfo.bioExtended}
            </p>

            {/* Tech tags */}
            <div className="pt-4">
              <p className="text-sm font-semibold text-slate-800 mb-3">Outils & Technologies</p>
              <div className="flex flex-wrap gap-2">
                {technologies.map((tech, i) => (
                  <Badge
                    key={i}
                    variant="secondary"
                    className="bg-slate-100 text-slate-600 hover:bg-teal-50 hover:text-teal-700 transition-colors duration-200 cursor-default font-medium"
                  >
                    {tech.name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      </FadeInSection>
    </div>
  </section>
);

/* ─── Expertise Section ─── */
const ExpertiseSection = () => (
  <section id="expertise" className="py-24 lg:py-32 bg-slate-50">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <FadeInSection>
        <div className="text-center mb-16">
          <span className="text-sm font-semibold text-teal-700 uppercase tracking-widest">
            Expertise
          </span>
          <h2
            className="text-3xl lg:text-4xl font-bold text-slate-900 mt-3"
            style={{ fontFamily: "'Source Serif 4', serif" }}
          >
            Domaines de Spécialisation
          </h2>
          <p className="text-slate-500 mt-4 max-w-xl mx-auto">
            Allier l'expertise en géotechnique aux méthodologies de pointe en data science.
          </p>
        </div>
      </FadeInSection>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {expertise.map((item, i) => {
          const IconComponent = iconMap[item.icon];
          return (
            <FadeInSection key={item.id} delay={i * 100}>
              <div className="group bg-white rounded-xl p-7 border border-slate-200/80 hover:border-teal-200 hover:shadow-lg hover:shadow-teal-50 transition-all duration-300">
                <div className="w-11 h-11 rounded-lg bg-teal-50 flex items-center justify-center mb-5 group-hover:bg-teal-100 transition-colors duration-300">
                  {IconComponent && (
                    <IconComponent className="w-5 h-5 text-teal-700" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </FadeInSection>
          );
        })}
      </div>
    </div>
  </section>
);

/* ─── Services Section ─── */
const ServicesSection = () => (
  <section className="py-24 lg:py-32 bg-white">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <FadeInSection>
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-2">
            <span className="text-sm font-semibold text-teal-700 uppercase tracking-widest">
              Services
            </span>
            <h2
              className="text-3xl lg:text-4xl font-bold text-slate-900 mt-3 leading-tight"
              style={{ fontFamily: "'Source Serif 4', serif" }}
            >
              Construire une collaboration
            </h2>
            <Separator className="mt-6 bg-teal-200 w-16 h-0.5" />
            <p className="text-slate-500 mt-6 leading-relaxed">
              Que vous ayez besoin d'une analyse ponctuelle ou d'un partenaire de recherche
              à long terme, je propose des modalités d'engagement flexibles adaptées aux
              besoins de votre projet.
            </p>
          </div>

          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-5">
            {services.map((service, i) => {
              const IconComponent = iconMap[service.icon];
              return (
                <FadeInSection key={service.id} delay={i * 100}>
                  <div className="group p-6 rounded-xl border border-slate-200/80 hover:border-teal-200 hover:shadow-md transition-all duration-300 bg-white">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center mb-4 group-hover:bg-teal-50 transition-colors duration-300">
                      {IconComponent && (
                        <IconComponent className="w-5 h-5 text-slate-600 group-hover:text-teal-700 transition-colors duration-300" />
                      )}
                    </div>
                    <h3 className="text-base font-semibold text-slate-900 mb-2">
                      {service.title}
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </FadeInSection>
              );
            })}
          </div>
        </div>
      </FadeInSection>
    </div>
  </section>
);

/* ─── Home Page ─── */
const HomePage = () => (
  <>
    <Header />
    <HeroSection />
    <AboutSection />
    <ExpertiseSection />
    <ServicesSection />
    <PublicationsConferences />
    <ContactFooter />
  </>
);

export default HomePage;
